import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  ElementRef,
  EnvironmentInjector,
  inject,
  input,
  OnDestroy,
  signal,
  Type,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

// eslint-disable-next-line no-restricted-imports
import { MatIconModule } from '@angular/material/icon';
import { DialogInsertionDirective } from '@core/dialog/shared/directives/dialog-insertion.directive';
import { BaseDialog } from '@core/dialog/shared/models/base-dialog.interface';
import { CompactClassPipe } from '@core/pipes/compact-class.pipe';

@Component({
  selector: 'tess-dialog-template',
  imports: [CommonModule, MatIconModule, DialogInsertionDirective, CompactClassPipe],
  templateUrl: './dialog-template.component.html',
  styleUrls: ['./dialog-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogTemplateComponent implements OnDestroy {
  readonly #envInjector = inject(EnvironmentInjector);

  readonly dialogTitle = input<string>('');
  readonly dialogStyle = input<string>('');
  readonly dialogBackground = input<string>('');
  protected readonly insertionPoint = viewChild<DialogInsertionDirective>(DialogInsertionDirective);
  protected readonly dialogRef = viewChild<ElementRef>('dialog');

  readonly animateDialog = signal<boolean>(false);

  readonly #componentRef = signal<ComponentRef<BaseDialog> | null>(null);

  constructor() {
    afterNextRender(() => {
      this.animateDialog.set(true);
    });
  }

  closeDialog(): void {
    this.#componentRef()?.instance?.close?.();
  }

  attachChildComponent<T extends BaseDialog>(childComponentType: Type<T>): ComponentRef<T> | null {
    const viewContainerRef = this.insertionPoint()?.viewContainerRef;

    if (!viewContainerRef || !childComponentType) {
      return;
    }

    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent<T>(childComponentType, {
      environmentInjector: this.#envInjector,
    });

    this.#componentRef.set(componentRef);

    return componentRef;
  }

  ngOnDestroy(): void {
    this.#componentRef()?.destroy();
  }
}
